import React, { useMemo } from 'react';
import useTopFiveMerchantByAcceptedRejected from 'Hooks/query-hooks/reports/useTopFiveMerchantByAcceptReject';
import { Column, Row } from 'react-table';
import { FOCUS_VIEW } from 'Common/enums';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';
import DashletContainer from 'Components/DashletContainer';
import clsx from 'clsx';
import { ProgressBar } from '@brighterion/br-ui';
import TopFiveMerchantByPercentARTable from './TopFiveMerchantByPercentARTable';
import styles from './index.css';

const TopFiveMerchantByPercentAcceptRejectDashlet = ({
    formValues,
    focusView,
    className,
    minColumns,
    chartType
}: Top5MerchantByPercentARProps) => {
    const { t } = useTranslation();
    const { isLoading, processedData } = useTopFiveMerchantByAcceptedRejected(chartType, focusView, formValues);
    let header = 'Percent Accept';
    let modifiedAccessor: MerchantAccessor = 'percentScaledAccept';
    if (focusView === FOCUS_VIEW.REJECT) {
        header = 'Percent Reject';
        modifiedAccessor = 'percentScaledReject';
    } else if (focusView === FOCUS_VIEW.REVIEW) {
        header = 'Percent Review';
        modifiedAccessor = 'percentScaledReview';
    }

    const columns: Column<Top5ValueVolumeTableItem>[] = useMemo(() => {
        const percentColumn: Column<Top5ValueVolumeTableItem> = {
            Header: header,
            accessor: modifiedAccessor,
            renderType: 'overrideCell',
            Cell: ({ value, row }: { value: number; row: Row }) => {
                let label = row.original.percentAccept;
                if (focusView === FOCUS_VIEW.ACCEPT) {
                    label = row.original.percentAccept;
                } else if (focusView === FOCUS_VIEW.REJECT) {
                    label = row.original.percentReject;
                } else if (focusView === FOCUS_VIEW.REVIEW) {
                    label = row.original.percentReview;
                }
                const labelClassName = clsx(styles.progressBarLabel, _.round(label, 2) !== 0 && styles.notZero);
                return (
                    <ProgressBar
                        className={styles.progressBarWrapper}
                        fillAreaClass={styles[`${focusView}ProgressBarFillArea`]}
                        labelClassName={labelClassName}
                        inlineLabel
                        labelPosition='back'
                        progress={Number.isNaN(_.round(Number(value) * 0.01, 4)) ? 0 : _.round(Number(value) * 0.01, 4)}
                        labelOverride={Number.isNaN(_.round(label, 2)) ? 0 : _.round(label, 2)}
                        isTransparent
                    />
                );
            }
        };
        return [...minColumns, { ...percentColumn }];
    }, [minColumns, focusView]);

    const dashletContainerCN = clsx(className, styles.container);
    const testId = `top5-merchant-${chartType}`;

    return (
        <DashletContainer
            dataTestId={testId}
            className={dashletContainerCN}
            isLoading={isLoading}
            header={{ label: t('top5MerchantsByPercentHeader', { focusView, chartType: t(_.capitalize(chartType)) }) }}
        >
            <TopFiveMerchantByPercentARTable data={processedData} columns={columns} dataTestId={`${testId}-table`} />
        </DashletContainer>
    );
};

export default TopFiveMerchantByPercentAcceptRejectDashlet;
